import { useState, useEffect } from 'react';
import { ListItemText, List, ListItem, ListItemIcon } from '@mui/material';
import { Typography, Button, DialogContent, DialogTitle } from '@mui/material';
import { NotInterested, FiberManualRecord } from '@mui/icons-material';
import { grey, green, amber } from '@mui/material/colors';
import { RoundedDialog, StandardDialogActions } from '../../Components';
import StateManager from '../../StateManager';
import { TaskIcon, FormIcon, ProcessIcon } from '../../Icons';
import { isArray } from 'lodash';
import axios from 'axios';
import ActionDialog from './ActionDialog';
import { useHistory } from 'react-router-dom';

export default function CellActionButton({
  params,
  initial,
  onChange,
  editable,
  rowId,
  columnId,
  onDataChange,
  building,
  disabled,
}) {
  const [starting, setStarting] = useState(false);
  const [itemsDialog, setItemsDialog] = useState(false);
  const [notApplicable, setNotApplicale] = useState(false);
  const [naDialog, setNaDialog] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const history = useHistory();

  useEffect(() => {
    if (!params) return;
    setNotApplicale(initial?.notApplicable);

    if (params.action === 'task' && isArray(initial?.resolvedTasks)) {
      setItems(initial.resolvedTasks);
    }
    if (params.action === 'form' && isArray(initial?.resolvedForms)) {
      setItems(initial.resolvedForms);
    }
    if (params.action === 'process' && isArray(initial?.resolvedProcesses)) {
      setItems(initial.resolvedProcesses);
    }
  }, [initial, params]);

  function checkOptional() {
    if (params.actionOptional) {
      setNaDialog(true);
      return;
    } else {
      startAction();
    }
  }

  function saveItems(items) {
    const field = params.action === 'task' ? 'tasks' : params.action === 'form' ? 'forms' : 'processes';
    onChange({ [field]: items });
    if (params.action === 'task') {
      setLoading(true);
      axios
        .post('/tasks/getTasksByIds', { ids: items })
        .then((res) => {
          setLoading(false);
          setItems(res.data);
          onDataChange({ resolvedTasks: res.data });
        })
        .catch((err) => {
          StateManager.setAxiosErrorAlert(err);
          setLoading(false);
        });
    }

    if (params.action === 'form') {
      setLoading(true);
      axios
        .post('/forms/entries/getEntriesByIds', { ids: items })
        .then((res) => {
          setLoading(false);
          setItems(res.data);
          onDataChange({ resolvedForms: res.data });
        })
        .catch((err) => {
          setLoading(false);
          StateManager.setAxiosErrorAlert(err);
        });
    }

    if (params.action === 'processes') {
      setLoading(true);
      axios
        .post('/process/getOngoingProcessesByIds', { ids: items })
        .then((res) => {
          setLoading(false);
          setItems(res.data);
          onDataChange({ resolvedProcesses: res.data });
        })
        .catch((err) => {
          setLoading(false);
          StateManager.setAxiosErrorAlert(err);
        });
    }
  }

  function startAction() {
    setNotApplicale(false);
    if (params.action === 'task') createTask();
    if (params.action === 'form') startEntry();
    if (params.action === 'process') startProcess();
  }

  function makeNotApplicable() {
    setNotApplicale(true);
    onChange({ ...initial, notApplicable: true });
  }

  function createTask() {
    if (starting) return;
    setStarting(true);
    axios
      .post('/tasks/createTask', { ...params.task, rowId })
      .then((res) => {
        setStarting(false);
        StateManager.setSuccessAlert('Task has been created');
        StateManager.selectTask(res.data._id);
        items.push(res.data._id);
        setItems([...items]);
        saveItems(items);
      })
      .catch((err) => {
        setStarting(false);
        StateManager.setAxiosErrorAlert(err);
      });
  }

  function startEntry() {
    if (starting) return;
    setStarting(true);
    axios
      .post('/forms/entries/sendEntry', { formId: params.form._id, rowId })
      .then((res) => {
        StateManager.setSuccessAlert('Entry has been started');
        if (res.data.length === 1) {
          items.push(res.data[0]._id);
          setItems([...items]);
          saveItems(items);
          setTimeout(() => history.push(`/forms/entry/${res.data[0]._id}`), 1000);
        }
      })
      .catch(() => {
        StateManager.setErrorAlert('Failed to start an entry');
        setStarting(false);
      });
  }

  function startProcess() {
    if (starting) return;
    setStarting(true);
    axios
      .post('/process/startProcess', { processId: params.process._id, rowId })
      .then((res) => {
        StateManager.setSuccessAlert('Process has been started');
        items.push(res.data.ongoingProcess._id);
        setItems([...items]);
        saveItems(items);
        setTimeout(() => history.push(`/processes/ongoing/${res.data.ongoingProcess._id}?started=true`), 1000);
      })
      .catch(() => {
        StateManager.setErrorAlert('Failed to start the process');
        setStarting(false);
      });
  }

  const actionColor = items.every((x) => x.completedAt || x.completedDate) ? green[500] : amber[500];

  if (building) {
    return <Typography color={'textSecondary'}>Actions</Typography>;
  }

  if (items.length > 0) {
    return (
      <>
        <Button
          startIcon={
            params.action === 'task' ? <TaskIcon /> : params.action === 'form' ? <FormIcon /> : <ProcessIcon />
          }
          onClick={() => setItemsDialog(true)}
          style={{ width: '100%' }}
          endIcon={
            loading ? (
              <CircularProgress color="primary" size={16} />
            ) : (
              <FiberManualRecord style={{ color: actionColor }} />
            )
          }
        >
          {items.length}{' '}
          {params.action === 'task' ? `task${items.length === 1 ? '' : 's'}` : items.length === 1 ? 'entry' : 'entries'}
        </Button>
        <ActionDialog
          open={itemsDialog}
          onClose={() => setItemsDialog(false)}
          params={params}
          initial={items}
          onChange={saveItems}
          editable={editable}
          rowId={rowId}
          columnId={columnId}
          onItemsChange={(items) => {
            setItems(items);
            const field =
              params.action === 'task'
                ? 'resolvedTasks'
                : params.action === 'form'
                ? 'resolvedForms'
                : 'resolvedProcesses';
            onDataChange({ [field]: items });
          }}
        />
      </>
    );
  } else {
    let button = null;

    if (editable && params) {
      if (notApplicable) {
        button = (
          <Button fullWidth onClick={() => setNaDialog(true)} disabled={Boolean(disabled)}>
            N/A
          </Button>
        );
      } else if (params.action === 'task') {
        button = (
          <Button
            style={{ width: '100%' }}
            startIcon={starting ? <CircularProgress color="primary" size={24} /> : <TaskIcon />}
            onClick={checkOptional}
            disabled={Boolean(disabled)}
          >
            create a task
          </Button>
        );
      } else if (params.action === 'form') {
        button = (
          <Button
            style={{ width: '100%' }}
            startIcon={starting ? <CircularProgress color="primary" size={24} /> : <FormIcon />}
            onClick={checkOptional}
            disabled={Boolean(disabled)}
          >
            start an entry
          </Button>
        );
      } else if (params.action === 'process') {
        button = (
          <Button
            style={{ width: '100%' }}
            startIcon={starting ? <CircularProgress color="primary" size={24} /> : <ProcessIcon />}
            onClick={checkOptional}
            disabled={Boolean(disabled)}
          >
            start a process
          </Button>
        );
      }

      return (
        <>
          {button}
          <NaDialog
            params={params}
            open={naDialog}
            onClose={() => setNaDialog(false)}
            onResult={(start) => (start ? startAction() : makeNotApplicable())}
          />
        </>
      );
    } else {
      return null;
    }
  }
}

function NaDialog({ params, open, onClose, onResult }) {
  return (
    <RoundedDialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Do you want to start the action or make it not applicable?</DialogTitle>

      <DialogContent>
        <List style={{ width: '100%' }}>
          <ListItem
            button
            style={{ borderRadius: 5 }}
            onClick={() => {
              onResult(true);
              onClose();
            }}
          >
            <ListItemIcon>
              {params?.action === 'form' ? <FormIcon /> : params?.action === 'process' ? <ProcessIcon /> : <TaskIcon />}
            </ListItemIcon>
            <ListItemText primary={`Start ${params?.action}`} />
          </ListItem>

          <ListItem
            button
            style={{ borderRadius: 5 }}
            onClick={() => {
              onResult(false);
              onClose();
            }}
          >
            <ListItemIcon>
              <NotInterested style={{ color: grey[500] }} />
            </ListItemIcon>
            <ListItemText primary="Make action not applicable" />
          </ListItem>
        </List>
      </DialogContent>

      <StandardDialogActions onClose={onClose} />
    </RoundedDialog>
  );
}
